// @ts-strict-ignore
import { Tabs } from '@carvana/helios-navigation';
import { appendTestContext, useTestFunc } from '@carvana/test-attributes';
import classNames from 'classnames';
import { useEffect, useState } from 'react';

import PlateEntry from './PlateEntry';
import usaStates from './PlateEntry/usaStates';
import { getInvalidVINLetter, isVinValid } from './PlateVinEntry.helpers';
import { Content, SubmitInfo } from './PlateVinEntry.styles';
import VinEntry from './VinEntry';

const lettersToNumbers = {
  I: '1',
  O: '0',
  Q: '9'
};

type IncorrectLetter = keyof typeof lettersToNumbers | undefined;

const vinValidationErrorText = (letter: IncorrectLetter) =>
  letter && letter in lettersToNumbers
    ? `The letter '${letter}' is invalid. Try the number '${lettersToNumbers[letter]}' instead.`
    : '';
const requiredFieldMessage = 'This field is required.';

export type SubmitPlateVin = (
  info: { vin: string } | { plate: string; plateState: string }
) => void;

type QueryParams = {
  licensePlate?: string;
  plateState?: string;
  vin?: string;
};

type Tab = 'plate' | 'vin';

export type ExternalError =
  | undefined
  | {
      tab?: Tab;
      errorMessage?: string;
    };

type SubmitEvent = React.MouseEvent | React.KeyboardEvent;

type VehicleEntryProps = {
  externalError?: ExternalError;
  isLoading?: boolean;
  onBlur?: () => void;
  queryParams?: QueryParams;
  submitPlateVin: SubmitPlateVin;
  submitText?: string;
  wideOnDesktop?: boolean;
};

const VehicleEntry = ({
  externalError = {},
  isLoading = false,
  onBlur,
  queryParams = {},
  submitPlateVin,
  submitText = 'Get my offer',
  wideOnDesktop = false
}: VehicleEntryProps) => {
  const getTestAttributes = useTestFunc();
  const [plate, setPlate] = useState('');
  const [plateError, setPlateError] = useState(false);
  const [state, setState] = useState('');
  const [stateError, setStateError] = useState(false);
  const [vin, setVin] = useState('');
  const [vinError, setVinError] = useState(false);
  const [incorrectLetter, setIncorrectLetter]: [
    incorrectLetter: IncorrectLetter,
    setIncorrectLetter: React.Dispatch<React.SetStateAction<IncorrectLetter>>
  ] = useState(undefined as IncorrectLetter);
  const [vinTouched, setVinTouched] = useState(false);
  const [plateTouched, setPlateTouched] = useState(false);
  const [plateVisible, setPlateVisible] = useState(true);

  const getFirstQueryParam = (param: string | string[]) => {
    if (Array.isArray(param)) {
      return param[0]?.toUpperCase();
    }

    return param?.toUpperCase();
  };

  useEffect(() => {
    setPlate(getFirstQueryParam(queryParams?.licensePlate ?? '') || plate);
    setState(getFirstQueryParam(queryParams?.plateState ?? '') || state);

    const vinValue = getFirstQueryParam(queryParams?.vin ?? '');
    setVin(vinValue);

    setPlateVisible(!vinValue);
  }, [queryParams.licensePlate, queryParams.plateState, queryParams.vin]);

  useEffect(() => {
    if (externalError.tab) {
      if (externalError.tab === 'plate') {
        setPlateError(true);
        setPlateTouched(true);
      } else {
        setVinError(true);
        setVinTouched(true);
      }
    }
  }, [externalError.tab]);

  const plateVisibleToggle = (val: string) => {
    onBlur?.();
    setPlateVisible(val.toLowerCase() === 'plate');
  };

  const updatePlate = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = target;
    setPlateError(!value);
    setStateError(false);
    setPlateTouched(true);
    setPlate(value?.toUpperCase().slice(0, 8));
  };

  const updateState = (value: string) => {
    setStateError(!value || !usaStates.find(s => s === value.toUpperCase()));
    setPlateError(false);
    setState(value.toUpperCase().slice(0, 2));
  };

  const getErrorMessage = (tab: Tab) => {
    if (externalError?.tab === tab) {
      return externalError.errorMessage;
    }
    if (tab === 'plate') {
      return requiredFieldMessage;
    }
    if (!vin) {
      return requiredFieldMessage;
    }
    if (incorrectLetter) {
      return vinValidationErrorText(incorrectLetter);
    }
    if (vin.length !== 17) {
      return 'Invalid VIN.';
    }
    return '';
  };

  const updateVin = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    let { value } = target;
    setVinTouched(true);
    if (value) {
      const regResult = getInvalidVINLetter(value);
      setVinError(!!regResult);
      if (regResult) {
        const firstInvalidLetter = regResult[0].toUpperCase();
        setIncorrectLetter(firstInvalidLetter as IncorrectLetter);
      } else {
        setIncorrectLetter(undefined);
      }
      value = value
        .replace(/[^A-Z0-9]/gi, '')
        .toUpperCase()
        .slice(0, 17);
    } else {
      setVinError(true);
      setIncorrectLetter(undefined);
    }
    setVin(value);
  };

  const handleSubmit = (e: SubmitEvent) => {
    e.preventDefault();
    if (plateVisible) {
      setPlateTouched(true);
      if (!plate || !state || stateError) {
        if (!plate) {
          setPlateError(true);
        }
        if (!state) {
          setStateError(true);
        }
        return;
      }
      setStateError(false);
      submitPlateVin({ plate, plateState: state });
    } else {
      setVinTouched(true);
      if (!isVinValid(vin)) {
        setVinError(true);
      } else {
        setVinError(false);
        submitPlateVin({ vin });
      }
    }
  };

  const handleBlur = () => {
    onBlur?.();
    if (plateVisible && !plate) {
      setPlateError(true);
    }
    if (!plateVisible && (vin.length !== 17 || !vin)) {
      setVinError(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      handleSubmit(e);
    }
  };

  const withVin = !plateVisible;
  const vinErrorToDisplay = vinTouched ? getErrorMessage('vin') : undefined;

  return (
    <Content $wideOnDesktop={wideOnDesktop}>
      <Tabs initialTab={plateVisible ? 'Plate' : 'VIN'}>
        <Tabs.TabBar>
          <Tabs.Tab
            tabGroup="Plate"
            className="tabStyle"
            onClick={() => plateVisibleToggle('plate')}
          >
            License Plate
          </Tabs.Tab>
          <Tabs.Tab tabGroup="VIN" className="tabStyle" onClick={() => plateVisibleToggle('VIN')}>
            VIN
          </Tabs.Tab>
        </Tabs.TabBar>
        <Tabs.TabPanel tabGroup="Plate">
          <PlateEntry
            plate={plate}
            plateError={plateTouched && plateError}
            error={plateTouched && plateError ? getErrorMessage('plate') : undefined}
            state={state}
            stateError={stateError}
            updatePlate={updatePlate}
            updateState={updateState}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            wideOnDesktop={wideOnDesktop}
          />
        </Tabs.TabPanel>
        <Tabs.TabPanel tabGroup="VIN">
          <VinEntry
            hasError={vinError}
            error={vinErrorToDisplay}
            updateVin={updateVin}
            vin={vin}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            wideOnDesktop={wideOnDesktop}
          />
        </Tabs.TabPanel>
      </Tabs>
      <SubmitInfo
        {...getTestAttributes('GetMyOfferButton')}
        className={classNames({ withVin })}
        onClick={handleSubmit}
        disabled={
          isLoading ||
          (vinError && vinTouched && withVin) ||
          (plateError && plateTouched && !withVin) ||
          (stateError && !withVin)
        }
        isLoading={isLoading}
        $wideOnDesktop={wideOnDesktop}
      >
        {submitText}
      </SubmitInfo>
    </Content>
  );
};

export default appendTestContext('Hero')(VehicleEntry) as React.ComponentType<VehicleEntryProps>;
